/**
 * Pure helpers shared across the harness group: formatting, language
 * detection, attachment constraints, and defensive value summarization.
 * No DOM access; safe to import from anywhere including tests.
 */
import type {
  IHarnessMessage,
  IHarnessTodoItem,
  IHarnessToolCall,
  THarnessMessageStreamDelta,
  THarnessToolStatus,
} from './interfaces.js';
import {
  computeLineDiff,
  diffStats,
  summarizeUnifiedDiff,
  type IDiffStats,
} from '../00group-dataview/dees-dataview-codebox/codebox.diff.js';

export const harnessMaxAttachmentBytes = 10 * 1024 * 1024;
export const harnessMaxTotalAttachmentBytes = 30 * 1024 * 1024;

export const harnessSupportedImageTypes = new Set([
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
]);

export const harnessSupportedTextTypes = new Set([
  'application/csv',
  'application/json',
  'application/ld+json',
  'application/xml',
  'application/yaml',
  'text/csv',
  'text/html',
  'text/javascript',
  'text/markdown',
  'text/plain',
  'text/xml',
  'text/yaml',
]);

const mediaTypeByExtension: Record<string, string> = {
  css: 'text/css',
  csv: 'text/csv',
  html: 'text/html',
  jpeg: 'image/jpeg',
  jpg: 'image/jpeg',
  js: 'text/javascript',
  json: 'application/json',
  jsonl: 'application/json',
  log: 'text/plain',
  md: 'text/markdown',
  mjs: 'text/javascript',
  png: 'image/png',
  svg: 'image/svg+xml',
  ts: 'text/plain',
  tsx: 'text/plain',
  txt: 'text/plain',
  webp: 'image/webp',
  xml: 'application/xml',
  yaml: 'application/yaml',
  yml: 'application/yaml',
};

const languageByExtension: Record<string, string> = {
  c: 'c',
  cjs: 'javascript',
  cpp: 'cpp',
  cs: 'csharp',
  css: 'css',
  go: 'go',
  h: 'c',
  html: 'html',
  java: 'java',
  js: 'javascript',
  json: 'json',
  jsx: 'javascript',
  kt: 'kotlin',
  md: 'markdown',
  mjs: 'javascript',
  php: 'php',
  py: 'python',
  rb: 'ruby',
  rs: 'rust',
  sh: 'bash',
  sql: 'sql',
  swift: 'swift',
  toml: 'ini',
  ts: 'typescript',
  tsx: 'typescript',
  xml: 'xml',
  yaml: 'yaml',
  yml: 'yaml',
  zsh: 'bash',
};

export const harnessIsRecord = (value: unknown): value is Record<string, unknown> => {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
};

/**
 * Whether a message is nothing but a tool call, and therefore renders as one transcript row
 * rather than as a paragraph. The transcript spaces and sizes rows by this, and the message host
 * renders by it, so both read the same message the same way.
 */
export const harnessRendersToolRow = (messageArg: IHarnessMessage): boolean => {
  return messageArg.role === 'tool'
    && messageArg.toolCall !== undefined
    && !messageArg.text
    && !messageArg.error
    && !messageArg.reasoning?.length
    && !messageArg.attachments?.length;
};

/** Uses the child projection as the status authority for subagent calls. */
export const harnessEffectiveToolStatus = (callArg: IHarnessToolCall): THarnessToolStatus => {
  if (callArg.subtask) {
    if (callArg.subtask.status.type === 'busy') return 'running';
    if (callArg.subtask.status.type === 'error') return 'error';
    return 'completed';
  }
  if (callArg.isError || callArg.status === 'error') return 'error';
  return callArg.status;
};

export const harnessFormatBytes = (bytes: unknown): string => {
  const value = typeof bytes === 'number' && Number.isFinite(bytes) ? bytes : 0;
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(value < 10 * 1024 ? 1 : 0)} KB`;
  return `${(value / 1024 / 1024).toFixed(value < 10 * 1024 * 1024 ? 1 : 0)} MB`;
};

export const harnessFormatDuration = (startedAt?: number, finishedAt?: number): string => {
  if (!startedAt || !finishedAt || finishedAt < startedAt) return '';
  const ms = finishedAt - startedAt;
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(ms < 10_000 ? 1 : 0)}s`;
  return `${Math.floor(ms / 60_000)}m ${Math.round((ms % 60_000) / 1000)}s`;
};

export const harnessFormatRelativeTime = (epochMs?: number): string => {
  if (!epochMs) return '';
  const deltaMs = Date.now() - epochMs;
  if (deltaMs < 45_000) return 'just now';
  const minutes = Math.round(deltaMs / 60_000);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(epochMs).toLocaleDateString();
};

/** Wall-clock time for cards: HH:MM within today, date + time otherwise. */
export const harnessFormatClockTime = (epochMs?: number): string => {
  if (!epochMs) return '';
  const date = new Date(epochMs);
  const time = date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  return date.toDateString() === new Date().toDateString()
    ? time
    : `${date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} ${time}`;
};

export const harnessLanguageForPath = (filePath: unknown): string => {
  const extension = String(filePath || '').split('.').pop()?.toLowerCase() || '';
  return languageByExtension[extension] || 'plaintext';
};

export const harnessMediaTypeForFile = (file: File): string => {
  if (file.type) return file.type;
  const extension = file.name.split('.').pop()?.toLowerCase() || '';
  return mediaTypeByExtension[extension] || 'application/octet-stream';
};

export const harnessIsSupportedAttachmentType = (mediaType: string): boolean => {
  return (
    harnessSupportedImageTypes.has(mediaType)
    || mediaType.startsWith('text/')
    || harnessSupportedTextTypes.has(mediaType)
  );
};

/** Chip/preview classification; every media type is attachable. */
export const harnessAttachmentKindFor = (mediaType: string): 'image' | 'text' | 'binary' => {
  if (harnessSupportedImageTypes.has(mediaType) || mediaType.startsWith('image/')) return 'image';
  if (mediaType.startsWith('text/') || harnessSupportedTextTypes.has(mediaType)) return 'text';
  return 'binary';
};

/** Mutates one normalized message when no rendered message element owns it. */
export const harnessApplyMessageDelta = (
  messages: IHarnessMessage[],
  delta: THarnessMessageStreamDelta,
): IHarnessMessage | undefined => {
  const message = messages.find((candidate) => candidate.id === delta.messageId);
  if (!message) return undefined;
  switch (delta.type) {
    case 'text':
      message.text += delta.delta;
      message.streaming = true;
      break;
    case 'reasoning-start':
      message.reasoning ??= [];
      if (!message.reasoning.some((part) => part.id === delta.partId)) {
        message.reasoning.push({
          id: delta.partId,
          text: '',
          startedAt: delta.startedAt ?? Date.now(),
        });
      }
      break;
    case 'reasoning': {
      message.reasoning ??= [];
      const part = message.reasoning.find((candidate) => candidate.id === delta.partId);
      if (part) part.text += delta.delta;
      else message.reasoning.push({ id: delta.partId, text: delta.delta, startedAt: Date.now() });
      break;
    }
    case 'reasoning-end': {
      const part = message.reasoning?.find((candidate) => candidate.id === delta.partId);
      if (part && !part.endedAt) part.endedAt = delta.endedAt ?? Date.now();
      break;
    }
    case 'tool-update':
      message.toolCall = {
        ...(message.toolCall ?? { id: delta.tool.id, name: '', status: 'pending' }),
        ...delta.tool,
      };
      break;
    case 'message-end':
      message.streaming = false;
      if (delta.usage) message.usage = delta.usage;
      if (delta.error) message.error = delta.error;
      message.updatedAt = Date.now();
      break;
  }
  return message;
};

export const harnessFileToBase64 = (file: File): Promise<string> =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : '';
      const marker = ';base64,';
      const markerIndex = result.indexOf(marker);
      resolve(markerIndex === -1 ? result : result.slice(markerIndex + marker.length));
    };
    reader.onerror = () => reject(reader.error || new Error(`Could not read ${file.name || 'file'}.`));
    reader.readAsDataURL(file);
  });

export const harnessTruncate = (text: string, maxChars = 6000): { text: string; truncated: boolean } => {
  if (text.length <= maxChars) return { text, truncated: false };
  return { text: text.slice(0, maxChars), truncated: true };
};

export const harnessStringifyValue = (value: unknown): string => {
  if (value === undefined || value === null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value, undefined, 2);
  } catch {
    return String(value);
  }
};

/**
 * Defensive one-glance summary of an arbitrary tool input/output object:
 * well-known keys first, then remaining scalars. Ported from the git.zone
 * IDE's agent formatting layer.
 */
/**
 * Extracts todo items from a todo-tool input/output. Accepts { todos: [...] }
 * or a bare array; items may carry content/subject/title and unknown statuses
 * normalize to 'pending'.
 */
export const harnessParseTodos = (value: unknown): IHarnessTodoItem[] => {
  const rawList = Array.isArray(value)
    ? value
    : harnessIsRecord(value) && Array.isArray(value.todos)
      ? value.todos
      : [];
  const result: IHarnessTodoItem[] = [];
  for (const entry of rawList) {
    if (typeof entry === 'string') {
      result.push({ content: entry, status: 'pending' });
      continue;
    }
    if (!harnessIsRecord(entry)) continue;
    const content = String(entry.content ?? entry.subject ?? entry.title ?? '').trim();
    if (!content) continue;
    const status = entry.status === 'completed'
      || entry.status === 'in_progress'
      || entry.status === 'cancelled'
      ? entry.status
      : 'pending';
    result.push({
      id: entry.id !== undefined ? String(entry.id) : undefined,
      content,
      status,
    });
  }
  return result;
};

export const harnessSummarizeValue = (value: unknown): string => {
  if (!harnessIsRecord(value)) return harnessStringifyValue(value);
  const lines: string[] = [];
  const seenKeys = new Set<string>();
  for (const key of ['description', 'command', 'workdir', 'cwd', 'filePath', 'path', 'pattern', 'query', 'url', 'action']) {
    const item = value[key];
    if (typeof item === 'string' && item) {
      lines.push(`${key}: ${item}`);
      seenKeys.add(key);
    }
  }
  for (const [key, item] of Object.entries(value)) {
    if (seenKeys.has(key) || item === undefined || item === null) continue;
    if (typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean') {
      const text = String(item);
      lines.push(`${key}: ${text.length > 240 ? `${text.slice(0, 240)}…` : text}`);
    }
  }
  return lines.join('\n');
};

/** First meaningful line of a summarized value; used for card subtitles. */
export const harnessSummaryLine = (value: unknown): string => {
  return harnessSummarizeValue(value).split('\n')[0] || '';
};

// eslint-disable-next-line no-control-regex
const ansiEscapePattern = /\x1b\[[0-9;?]*[A-Za-z]|\x1b\][^\x07]*(?:\x07|\x1b\\)|\x1b[=>]/g;

/** Terminal output carries raw escape sequences; they must never reach a header or a summary. */
export const harnessStripAnsi = (text: string): string => text.replace(ansiEscapePattern, '');

export interface IHarnessTerminalOutcome {
  stdout: string;
  stderr: string;
  /** Top-level exitCode wins over a nested one, matching how producers report it. */
  exitCode?: number;
}

/**
 * Normalizes the three shapes producers use for shell output: a `{stdout, stderr, exitCode}`
 * record, a combined string with a separate top-level `exitCode`, or an arbitrary value.
 */
export const harnessTerminalOutcome = (callArg: IHarnessToolCall): IHarnessTerminalOutcome => {
  const output = callArg.output;
  let stdout = '';
  let stderr = '';
  let nestedExitCode: number | undefined;
  if (harnessIsRecord(output)) {
    stdout = typeof output.stdout === 'string' ? output.stdout : harnessStringifyValue(output.stdout ?? '');
    stderr = typeof output.stderr === 'string' ? output.stderr : '';
    nestedExitCode = typeof output.exitCode === 'number' ? output.exitCode : undefined;
  } else {
    stdout = harnessStringifyValue(output);
  }
  const exitCode = typeof callArg.exitCode === 'number' ? callArg.exitCode : nestedExitCode;
  return { stdout, stderr, ...(exitCode === undefined ? {} : { exitCode }) };
};

export interface IHarnessToolStatusPresentation {
  status: THarnessToolStatus;
  label: string;
  tone: 'default' | 'running' | 'pending' | 'success' | 'warning' | 'error';
  /** A truthful non-zero process exit code, rendered separately from the status. */
  exitCode?: number;
}

/** Shared card/sidebar presentation for a tool's effective status. */
export const harnessToolStatusPresentation = (
  callArg: IHarnessToolCall,
  terminal = false,
): IHarnessToolStatusPresentation => {
  // A partial output may carry isError before a command settles. Its canonical
  // active status remains authoritative until the producer reports completion.
  const status = terminal && (callArg.status === 'pending' || callArg.status === 'running')
    ? callArg.status
    : harnessEffectiveToolStatus(callArg);
  const exitCode = terminal ? harnessTerminalOutcome(callArg).exitCode : undefined;
  const settled = callArg.status === 'completed' || callArg.status === 'error';
  if (terminal && settled && exitCode !== undefined && exitCode !== 0) {
    return { status: 'completed', label: 'Completed', tone: 'default', exitCode };
  }
  return {
    status,
    label: status,
    tone: status === 'completed' ? 'success'
      : status === 'stopped' ? 'warning'
        : status,
  };
};

const harnessLastMeaningfulLine = (text: string): string => {
  const lines = harnessStripAnsi(text).split('\n');
  for (let index = lines.length - 1; index >= 0; index -= 1) {
    const line = lines[index].trim();
    if (line) return line;
  }
  return '';
};

export interface IHarnessApplyPatchFile {
  op: 'add' | 'update' | 'delete';
  path: string;
  before: string;
  after: string;
  unifiedDiff?: string;
  lineNumbersKnown: boolean;
}

/**
 * Parses the apply_patch envelope (`*** Begin Patch` / `*** Add|Update|Delete
 * File: path` / `+`/`-`/context lines) that patch-oriented models emit,
 * reconstructing before/after content per file so updates render as diffs.
 */
export const harnessParseApplyPatch = (patchText: string): IHarnessApplyPatchFile[] => {
  const files: IHarnessApplyPatchFile[] = [];
  let current: {
    op: IHarnessApplyPatchFile['op'];
    path: string;
    before: string[];
    after: string[];
    patch: string[];
    sawHunk: boolean;
    lineNumbersKnown: boolean;
  } | null = null;
  const flush = (): void => {
    if (!current) return;
    const unifiedDiff = current.op === 'update' && current.sawHunk && current.lineNumbersKnown
      ? [`--- a/${current.path}`, `+++ b/${current.path}`, ...current.patch].join('\n')
      : undefined;
    files.push({
      op: current.op,
      path: current.path,
      before: current.before.join('\n'),
      after: current.after.join('\n'),
      unifiedDiff,
      lineNumbersKnown: Boolean(unifiedDiff),
    });
    current = null;
  };
  for (const line of patchText.split('\n')) {
    const header = line.match(/^\*\*\* (Add|Update|Delete) File: (.+)$/);
    if (header) {
      flush();
      current = {
        op: header[1].toLowerCase() as IHarnessApplyPatchFile['op'],
        path: header[2].trim(),
        before: [],
        after: [],
        patch: [],
        sawHunk: false,
        lineNumbersKnown: true,
      };
      continue;
    }
    if (
      /^\*\*\* (Begin|End) Patch/.test(line)
      || line === '*** End of File'
      || /^\*\*\* Move to: /.test(line)
    ) {
      if (/End Patch/.test(line)) flush();
      continue;
    }
    if (!current) continue;
    if (line.startsWith('@@')) {
      current.sawHunk = true;
      if (/^@@ -\d+(?:,\d+)? \+\d+(?:,\d+)? @@/.test(line)) {
        current.patch.push(line);
      } else {
        current.lineNumbersKnown = false;
      }
      continue;
    }
    current.patch.push(line);
    if (line.startsWith('+')) {
      current.after.push(line.slice(1));
    } else if (line.startsWith('-')) {
      current.before.push(line.slice(1));
    } else {
      const text = line.startsWith(' ') ? line.slice(1) : line;
      current.before.push(text);
      current.after.push(text);
    }
  }
  flush();
  return files;
};

/**
 * The one reading of a file-write call's input. The body renderer and the row's diff stat both
 * go through it, so what the row counts is always what the body shows.
 */
export type THarnessFileWriteSource =
  | { kind: 'changes'; changes: Array<{ path: string; diff: string }> }
  | { kind: 'patch'; files: IHarnessApplyPatchFile[] }
  | {
      kind: 'content';
      path: string;
      before: string;
      after: string;
      /** A replacement edit (oldString/newString) rather than a whole-file write. */
      stringReplacement: boolean;
    };

export const harnessFileWriteSource = (callArg: IHarnessToolCall): THarnessFileWriteSource => {
  const input = harnessIsRecord(callArg.input) ? callArg.input : {};
  if (Array.isArray(input.changes)) {
    const changes = input.changes
      .filter(harnessIsRecord)
      .filter((change): change is { path: string; diff: string } => (
        typeof change.path === 'string' && typeof change.diff === 'string'
      ));
    if (changes.length) return { kind: 'changes', changes };
  }
  // Patch-envelope tools (apply_patch) carry everything in patchText.
  if (typeof input.patchText === 'string' && input.patchText.includes('*** ')) {
    const files = harnessParseApplyPatch(input.patchText);
    if (files.length > 0) return { kind: 'patch', files };
  }
  // Covers both conventions: whole-file writes (content/newContent) and
  // string-replacement edits à la OpenCode/Claude (oldString/newString).
  return {
    kind: 'content',
    path: String(input.path ?? input.filePath ?? input.file ?? ''),
    before: String(
      input.oldContent
        ?? input.originalContent
        ?? input.before
        ?? input.oldString
        ?? input.old_string
        ?? '',
    ),
    after: String(input.content ?? input.newContent ?? input.newString ?? input.new_string ?? ''),
    stringReplacement: input.oldString !== undefined || input.old_string !== undefined,
  };
};

/**
 * Exact added/removed line counts for a file-write call, for the row's diff stat. Truncated or
 * unparseable patches yield nothing at all rather than a number that is off.
 */
export const harnessFileWriteDiffStats = (callArg: IHarnessToolCall): IDiffStats | undefined => {
  const source = harnessFileWriteSource(callArg);
  if (source.kind === 'changes') {
    const total: IDiffStats = { added: 0, removed: 0 };
    for (const change of source.changes) {
      const summary = summarizeUnifiedDiff(change.diff);
      if (summary.type !== 'exact') return undefined;
      total.added += summary.stats.added;
      total.removed += summary.stats.removed;
    }
    return total;
  }
  if (source.kind === 'patch') {
    const total: IDiffStats = { added: 0, removed: 0 };
    for (const file of source.files) {
      // A reconstructed envelope carries every changed line, so before/after is exact even when
      // the hunk headers carry no coordinates.
      const stats = file.unifiedDiff
        ? summarizeUnifiedDiff(file.unifiedDiff)
        : { type: 'exact' as const, stats: diffStats(computeLineDiff(file.before, file.after)) };
      if (stats.type !== 'exact') return undefined;
      total.added += stats.stats.added;
      total.removed += stats.stats.removed;
    }
    return total;
  }
  if (!source.before && !source.after) return undefined;
  // A write without a known previous content adds every line of the new content.
  return diffStats(computeLineDiff(source.before, source.after));
};

/**
 * One-line outcome for a finished shell call, for the collapsed card header. The command itself
 * is already the card subtitle, so this describes the RESULT: exit code, how much came back, and
 * the last meaningful line. Harness support for a richer summary is uneven — only OpenCode
 * supplies a model-authored description, so this is derived from output that every harness has.
 */
export const harnessTerminalResultSummary = (callArg: IHarnessToolCall): string => {
  const { stdout, stderr, exitCode } = harnessTerminalOutcome(callArg);
  const parts: string[] = [];
  // A non-zero exit is already presented as the dedicated orange Exit Code badge.
  // Keep successful exit 0 in the summary because it has no separate badge.
  if (exitCode === 0) parts.push('exit 0');
  const combined = `${stdout}${stdout && stderr ? '\n' : ''}${stderr}`;
  const lineCount = combined ? harnessStripAnsi(combined).split('\n').filter((line) => line.trim()).length : 0;
  if (lineCount > 0) parts.push(`${lineCount} ${lineCount === 1 ? 'line' : 'lines'}`);
  // Without an exit code there is nothing else to report, so a silent command still says so
  // rather than collapsing to a bare command line. No exit code is ever invented here.
  else parts.push('no output');
  const lastLine = harnessLastMeaningfulLine(stderr) || harnessLastMeaningfulLine(stdout);
  const prefix = parts.join(' · ');
  if (!lastLine) return prefix;
  const trimmed = lastLine.length > 160 ? `${lastLine.slice(0, 160)}…` : lastLine;
  return prefix ? `${prefix} · ${trimmed}` : trimmed;
};
